// BizzIQ Business Intelligence Section Types
// Matches the 12 meta-categories schema v1

export type SectionSlug =
  | "identity"
  | "people"
  | "offerings"
  | "audience"
  | "brand"
  | "competition"
  | "technology"
  | "marketing"
  | "sales"
  | "operations"
  | "finance"
  | "strategy"

export type CompletionStatus = "not_started" | "in_progress" | "complete"

export type MembershipRole = "owner" | "admin" | "member" | "guest"

// ============================================
// SECTION-LEVEL METADATA (All Sections)
// ============================================
export interface SectionMetadata {
  section_id: string
  section_name: string
  section_number: number
  completion_status: CompletionStatus
  completion_percentage: number
  required_for_mvp: boolean
  last_updated?: string
  updated_by?: string
  version: number
  is_locked?: boolean
  // AI metadata
  ai_confidence_score?: number
  ai_generated_fields?: string[]
  needs_review?: boolean
  review_reason?: string
  // RAG metadata
  embedding_last_updated?: string
  indexed_for_search?: boolean
  search_tags?: string[]
}

// ============================================
// 1. IDENTITY (15% weight)
// ============================================
export type OrganizationType = "for_profit" | "nonprofit" | "social_enterprise" | "government" | "cooperative"
export type OperatingModel = "remote" | "hybrid" | "on_site"

export interface KeyMilestone {
  date?: string
  title: string
  description?: string
}

export interface IdentityData {
  // 1.1 Organization Profile
  display_name?: string
  legal_name?: string
  tagline?: string
  organization_type?: OrganizationType
  industry?: string
  subindustry?: string
  founded_year?: number
  hq_location?: {
    city?: string
    state?: string
    country?: string
  }
  operating_model?: OperatingModel

  // 1.2 Mission & Vision
  mission_statement?: string
  vision_statement?: string

  // 1.3 Purpose & Context
  purpose_reason?: string
  founder_intent?: string
  why_now_context?: string

  // 1.4 Impact (nonprofits/social enterprises)
  impact_statement?: string
  beneficiaries?: string[]
  intended_outcomes?: string[]

  // 1.5 Values & Principles
  core_values?: string[]
  guiding_principles?: string[]

  // 1.6 Story & Narrative
  founding_story?: string
  origin_moment?: string
  key_milestones?: KeyMilestone[]

  // Legacy fields
  mission?: string
  vision?: string
  values?: string[]
  story?: string
}

// ============================================
// 2. PEOPLE (10% weight)
// ============================================
export type Department =
  | "leadership"
  | "marketing"
  | "sales"
  | "operations"
  | "finance"
  | "product"
  | "engineering"
  | "customer_success"
  | "other"

export type EmploymentType = "full_time" | "part_time" | "contractor" | "advisor" | "volunteer"

export type Seniority = "intern" | "junior" | "mid_level" | "senior" | "lead" | "executive"

export type OrgStructureType = "solo" | "flat" | "functional" | "team_based" | "matrix"

export type PartnerType =
  | "vendor"
  | "supplier"
  | "technology_partner"
  | "strategic_partner"
  | "distribution_partner"
  | "other"

export type RelationshipStrength = "critical" | "important" | "nice_to_have"

export interface TeamMember {
  id?: string
  name: string
  title?: string
  role_title?: string
  department?: Department
  employment_type?: EmploymentType
  seniority?: Seniority
  location?: string
  start_date?: string
  key_responsibilities?: string[]
  responsibilities?: string[]
  skills?: string[]
  added_date?: string
  last_updated?: string
}

export interface OwnerProfile {
  name?: string
  title?: string
  email?: string
  bio?: string
  responsibilities?: string[]
}

export interface Advisor {
  id?: string
  name: string
  expertise?: string
  involvement_level?: "active" | "periodic" | "as_needed" | "weekly" | "monthly" | "quarterly"
  contribution?: string
  notes?: string
}

export interface Partner {
  id?: string
  organization: string
  type?: PartnerType
  responsibility_area?: string
  relationship_strength?: RelationshipStrength
  description?: string
  notes?: string
}

export interface OrgStructure {
  structure_type?: OrgStructureType
  total_headcount?: number
  capacity_summary?: string
}

export interface PeopleData {
  owner_profile?: OwnerProfile
  team_members?: TeamMember[]
  advisors?: Advisor[]
  partners?: Partner[]
  key_partners?: Partner[]
  org_structure?: OrgStructure
  org_chart_notes?: string
}

// ============================================
// 3. OFFERINGS (15% weight)
// ============================================
export type PricingType = "one_time" | "subscription" | "usage_based" | "tiered" | "freemium" | "custom"
export type BillingPeriod = "monthly" | "annual" | "one_time" | "per_use"
export type ProductCategory =
  | "software_saas"
  | "software_license"
  | "hardware"
  | "digital_product"
  | "physical_goods"
  | "subscription_box"
  | "consumables"
  | "other"
export type ProductStatus = "active" | "planned" | "beta" | "deprecated"
export type ProductTier = "entry" | "standard" | "premium" | "enterprise"

export type ServicePricingModel =
  | "hourly"
  | "project_based"
  | "monthly_retainer"
  | "package_tiered"
  | "value_based"
  | "subscription"
  | "project"
  | "retainer"
  | "package"
export type ServiceCategory =
  | "consulting"
  | "design"
  | "development"
  | "marketing"
  | "operations"
  | "finance"
  | "legal"
  | "training"
  | "done_for_you"
  | "support"
  | "implementation"
  | "managed_service"
  | "other"
export type ServiceTier = "basic" | "standard" | "premium" | "custom"
export type ServiceStatus = "active" | "planned" | "paused"

export interface ProductPricing {
  price_amount?: number
  amount?: number
  currency?: string
  pricing_type?: PricingType
  type?: PricingType
  billing_period?: BillingPeriod
  unit?: "month" | "year" | "user" | "seat" | "transaction"
}

export interface Product {
  id?: string
  name: string
  short_description?: string
  detailed_description?: string
  pricing?: ProductPricing
  target_customer?: string
  key_benefits?: string[]
  primary_use_case?: string
  category?: ProductCategory
  competitive_advantage?: string
  product_tier?: ProductTier
  status?: ProductStatus
  revenue_contribution_estimate?: number
  created_date?: string
  last_updated?: string
  ai_confidence?: number
}

export interface Service {
  id?: string
  name: string
  short_description?: string
  service_description?: string
  pricing_model?: ServicePricingModel
  pricing?: {
    price_amount?: number
    currency?: string
    unit?: string
  }
  price?: number
  price_unit?: string
  typical_duration?: string
  key_deliverables?: string[]
  service_process?: string
  ideal_client_profile?: string
  problems_solved?: string[]
  category?: ServiceCategory
  monthly_capacity?: number
  service_tier?: ServiceTier
  status?: ServiceStatus
  revenue_contribution_estimate?: number
  created_date?: string
  last_updated?: string
  ai_confidence?: number
}

export interface Program {
  id?: string
  name: string
  description?: string
  type?: "accelerator" | "course" | "membership" | "community" | "grant" | "other"
  pricing?: ProductPricing
}

export interface Bundle {
  id?: string
  name: string
  description?: string
  included_items?: string[]
  pricing?: ProductPricing
}

export interface OfferingsData {
  products?: Product[]
  services?: Service[]
  programs?: Program[]
  bundles?: Bundle[]
}

// ============================================
// 4. AUDIENCE (12% weight)
// ============================================
export type SegmentType = "customer" | "prospect" | "donor" | "beneficiary"
export type SegmentSizeEstimate = "small_<100" | "medium_100-1000" | "large_1000+"

export interface SegmentDemographics {
  age_range?: string
  location?: string
  income_level?: string
  industry?: string
  company_size?: string
}

export interface AudienceSegment {
  id?: string
  name: string
  description?: string

  // Customer understanding
  needs?: string[]
  pains?: string[]
  goals?: string[]
  jobs_to_be_done?: string[]
  behaviors?: string
  motivations?: string[]
  objections?: string[]

  // Segmentation
  type?: SegmentType
  segment_type?: SegmentType
  segment_size_estimate?: SegmentSizeEstimate
  channel_preferences?: string[]
  decision_criteria?: string[]

  demographics?: SegmentDemographics
  lifetime_value_estimate?: number
  is_primary_segment?: boolean

  created_date?: string
  last_updated?: string
}

export interface Persona {
  id?: string
  name: string
  archetype?: string
  segment_id?: string
  demographic_info?: Record<string, unknown>
  preferences?: string[]
  quote?: string
}

export interface JourneyStage {
  name: string
  description?: string
  touchpoints?: string[]
  key_questions?: string[]
  metrics?: string[]
}

export interface CustomerJourney {
  id?: string
  name?: string
  description?: string
  awareness_stage?: JourneyStage
  consideration_stage?: JourneyStage
  decision_stage?: JourneyStage
  retention_stage?: JourneyStage
  stages?: JourneyStage[]
}

export interface AudienceData {
  segments?: AudienceSegment[]
  personas?: Persona[]
  customer_journey?: CustomerJourney
  journeys?: CustomerJourney[]
  nonprofit_specific?: {
    beneficiary_profiles?: AudienceSegment[]
    donor_profiles?: AudienceSegment[]
  }
}

// ============================================
// 5. BRAND (8% weight)
// ============================================
export interface BrandVisual {
  primary_colors?: string[]
  secondary_colors?: string[]
  logo_url?: string
  font_preferences?: string[]
}

export interface BrandMessaging {
  tagline?: string
  elevator_pitch?: string
  key_messages?: string[]
}

export interface BrandAsset {
  type?: "logo" | "banner" | "template" | "deck" | "icon" | "image" | "video" | "document"
  name: string
  url?: string
  description?: string
}

export interface BrandData {
  positioning_statement?: string
  tone_of_voice?: string[]
  brand_attributes?: string[]
  visual?: BrandVisual
  messaging?: BrandMessaging
  assets?: BrandAsset[]
}

// ============================================
// 6. COMPETITION (8% weight)
// ============================================
export type CompetitorType = "direct" | "indirect" | "substitute" | "potential"
export type WinRate = "usually_win" | "competitive" | "usually_lose" | "unknown"
export type MarketSize = "small_<10M" | "medium_10M-100M" | "large_100M-1B" | "very_large_>1B"
export type CompetitiveIntensity = "low" | "moderate" | "high" | "very_high"

export interface Competitor {
  id?: string
  name: string
  type?: CompetitorType
  description?: string
  strengths?: string[]
  weaknesses?: string[]
  pricing_summary?: string
  positioning_notes?: string
  win_rate_against?: WinRate
  website?: string
  created_date?: string
  last_updated?: string
}

export interface Alternative {
  id?: string
  name: string
  description?: string
  notes?: string
}

export interface SWOT {
  strengths?: string[]
  weaknesses?: string[]
  opportunities?: string[]
  threats?: string[]
}

export interface MarketContext {
  industry_trends?: string[]
  market_size_estimate?: MarketSize
  competitive_intensity?: CompetitiveIntensity
}

export interface CompetitionData {
  competitors?: Competitor[]
  alternatives?: string[] | Alternative[]
  swot?: SWOT
  market_context?: MarketContext
}

// ============================================
// 7. MARKETING (10% weight)
// ============================================
export type ChannelType =
  | "paid_search"
  | "paid_social"
  | "organic_social"
  | "content_marketing"
  | "email_marketing"
  | "seo"
  | "partnerships"
  | "events"
  | "direct_mail"
  | "pr"
  | "referral"
  | "owned"
  | "paid"
  | "earned"
  | "other"

export type ChannelStatus = "active" | "testing" | "paused" | "planned" | "experimental"

export interface ChannelMetric {
  metric_name: string
  current_value?: number
  target_value?: number
  unit?: string
}

export interface MarketingChannel {
  id?: string
  name: string
  type?: ChannelType
  status?: ChannelStatus
  priority?: "low" | "medium" | "high"
  monthly_budget?: number
  key_metrics?: ChannelMetric[]
  performance_notes?: string
  metrics?: string[]
  notes?: string
  created_date?: string
  last_updated?: string
}

export interface ContentStrategy {
  content_pillars?: string[]
  content_formats?: string[]
  publishing_cadence?: string
}

export interface MarketingBudget {
  total_monthly_budget?: number
  monthly_spend?: number
  budget_allocation_notes?: string
  primary_channels?: string[]
  notes?: string
}

export interface FunnelStage {
  name: string
  description?: string
  metrics?: string[]
}

export interface MarketingFunnel {
  id?: string
  name: string
  stages?: FunnelStage[]
}

export interface MarketingData {
  // 7.1 Strategy Overview
  strategy?: {
    marketing_strategy_overview?: string
    target_audience_priority?: string[]
    positioning_focus?: string
  }
  strategy_summary?: string

  // 7.2 Channels
  channels?: MarketingChannel[]

  // 7.3 Content Strategy
  content_strategy?: ContentStrategy

  // 7.4 Budget
  budget?: MarketingBudget
  budget_summary?: MarketingBudget

  funnels?: MarketingFunnel[]
}

// ============================================
// 8. SALES (10% weight)
// ============================================
export type SalesModelType =
  | "self_service"
  | "sales_assisted"
  | "enterprise_sales"
  | "partner_led"
  | "hybrid"
  | "product_led"
  | "sales_led"

export interface SalesModel {
  model_type?: SalesModelType
  process_summary?: string
  avg_sales_cycle_days?: number
  avg_deal_size?: number
  currency?: string
}

export interface PipelineStage {
  stage_name: string
  description?: string
  conversion_rate_target?: number
  avg_stage_duration_days?: number
}

export interface SalesPipeline {
  id?: string
  name?: string
  pipeline_name?: string
  stages?: PipelineStage[] | string[]
  qualification_criteria?: string[]
}

export interface SalesPricing {
  pricing_philosophy?: string
  discount_policy?: string
  payment_terms?: string
  plans?: {
    name: string
    price?: number
    unit?: string
    features?: string[]
  }[]
  notes?: string
}

export interface SalesObjection {
  objection: string
  recommended_response?: string
  frequency?: "very_common" | "common" | "occasional" | "rare"
}

export interface SalesMetrics {
  close_rate?: number
  pipeline_coverage?: number
}

export interface CloseRates {
  overall?: number
  by_stage?: Record<string, number>
  by_segment?: Record<string, number>
}

export interface SalesData {
  // 8.1 Sales Model
  sales_model?: SalesModelType | SalesModel

  // 8.2 Pipeline
  pipeline?: SalesPipeline
  pipelines?: SalesPipeline[]

  // 8.3 Pricing Strategy
  pricing?: SalesPricing
  pricing_overview?: SalesPricing

  // 8.4 Objections
  objections?: SalesObjection[]
  key_objections?: string[]
  common_objections?: SalesObjection[]

  // 8.5 Metrics
  metrics?: SalesMetrics
  close_rates_estimate?: CloseRates

  // Legacy
  sales_process?: string
}

// ============================================
// 9. OPERATIONS (8% weight)
// ============================================
export type FulfillmentModel = "done_by_you" | "done_with_you" | "self_service" | "hybrid"
export type DeliveryMethod = "digital" | "physical" | "in_person" | "hybrid"
export type ProcessFrequency = "daily" | "weekly" | "monthly" | "as_needed"
export type ProcessStatus = "documented" | "informal" | "needs_documentation"
export type ToolCategory =
  | "accounting"
  | "crm"
  | "email_marketing"
  | "website"
  | "payment"
  | "project_management"
  | "communication"
  | "design"
  | "analytics"
  | "other"

export interface OperatingModelData {
  operational_framework?: string
  fulfillment_model?: FulfillmentModel
  delivery_method?: DeliveryMethod
}

export interface CapacityData {
  max_clients_per_month?: number
  current_utilization?: number
  capacity_constraints?: string[]
  scalability_notes?: string
}

export interface CoreProcess {
  id?: string
  name?: string
  process_name?: string
  description?: string
  owner?: string
  frequency?: ProcessFrequency
  documentation_url?: string
  sop_url?: string
  status?: ProcessStatus
}

export interface OperationsTool {
  id?: string
  tool_name?: string
  name?: string
  category?: ToolCategory
  purpose?: string
  monthly_cost?: number
  integration_needs?: string[]
}

export interface QualityMetric {
  metric_name: string
  current_value?: number
  target_value?: number
  unit?: string
}

export interface QualityData {
  quality_metrics?: QualityMetric[]
  customer_satisfaction_score?: number
}

export interface Vendor {
  id?: string
  name: string
  role?: string
  notes?: string
  contact?: string
}

export interface OperationsData {
  // 9.1 Operating Model
  operating_model?: "remote_first" | "hybrid" | "onsite" | "distributed" | OperatingModelData

  // 9.2 Capacity
  capacity?: CapacityData
  capacity_notes?: string

  // 9.3 Key Processes
  processes?: CoreProcess[]
  core_processes?: CoreProcess[]

  // 9.4 Tools & Systems
  tools?: OperationsTool[]

  // 9.5 Quality & Performance
  quality?: QualityData

  vendors?: Vendor[]
}

// ============================================
// 10. FINANCE (12% weight - updated to match schema)
// ============================================
export type RevenueModelType =
  | "subscription"
  | "transactional"
  | "project_based"
  | "advertising"
  | "marketplace"
  | "licensing"
  | "donations"
  | "grants"
  | "hybrid"

export type FundingStatus =
  | "bootstrapped"
  | "pre_seed"
  | "seed"
  | "series_a"
  | "series_b_plus"
  | "profitable"
  | "grants"
  | "donations"

export type FieldSensitivity = "public" | "internal" | "confidential" | "restricted"

export interface RevenueStream {
  stream_name: string
  description?: string
  contribution_percentage?: number
}

export interface FinanceRevenueModel {
  model_type?: RevenueModelType
  revenue_streams?: RevenueStream[]
}

export interface FinancePerformance {
  current_monthly_revenue?: number
  current_annual_revenue?: number
  currency?: string
  revenue_growth_rate?: number
  monthly_churn_rate?: number
  net_revenue_retention?: number
}

export interface FinanceCostBreakdown {
  payroll?: number
  marketing?: number
  software_tools?: number
  office_rent?: number
  cost_of_goods_sold?: number
  other?: number
}

export interface FinanceCosts {
  total_monthly_expenses?: number
  currency?: string
  cost_breakdown?: FinanceCostBreakdown
  burn_rate?: number
}

export interface FinanceUnitEconomics {
  customer_acquisition_cost?: number
  lifetime_value?: number
  ltv_cac_ratio?: number
  gross_margin?: number
  payback_period_months?: number
  currency?: string
}

export interface FinanceFunding {
  funding_status?: FundingStatus
  capital_raised_to_date?: number
  runway_months?: number
  capital_needs_next_12mo?: number
  currency?: string
}

export interface FinanceBudgetAllocation {
  marketing_budget?: number
  sales_budget?: number
  operations_budget?: number
  product_budget?: number
  administrative_budget?: number
}

export interface FinanceBudgets {
  annual_budget_total?: number
  currency?: string
  budget_allocation?: FinanceBudgetAllocation
}

export interface FinanceData {
  section_meta?: SectionMetadata
  revenue_model?: FinanceRevenueModel
  performance?: FinancePerformance
  costs?: FinanceCosts
  unit_economics?: FinanceUnitEconomics
  funding?: FinanceFunding
  budgets?: FinanceBudgets
}

// ============================================
// 11. TECHNOLOGY (Deprecated - merged into Operations)
// ============================================
// Note: Technology section is deprecated as standalone
// All fields moved to Section 9 (Operations > Tools)
// Keeping for backwards compatibility
export interface TechnologyData {
  systems?: { name?: string; category?: string; purpose?: string }[]
  data_sources?: { name?: string; type?: string }[]
  integration_priorities?: string[]
}

// ============================================
// 12. STRATEGY (15% weight - Capstone Section)
// ============================================

export type GoalCategory = "revenue" | "growth" | "efficiency" | "quality" | "team" | "product" | "market" | "impact"

export type GoalStatus = "planning" | "active" | "at_risk" | "complete" | "abandoned"

export type KeyResultStatus = "on_track" | "at_risk" | "blocked" | "complete"

export type MilestoneStatus = "pending" | "in_progress" | "complete"

export type BlockerSeverity = "low" | "medium" | "high"

export type RoadmapType = "business" | "product" | "marketing" | "sales" | "operations"

export type RoadmapTimeframe = "30_day" | "90_day" | "annual" | "3_year"

export type RoadmapStatus = "draft" | "active" | "archived"

export type InitiativeStatus = "planned" | "in_progress" | "complete"

export type PlanType = "annual_plan" | "strategic_plan" | "growth_plan" | "fundraising"

export type StrategicInitiativeType =
  | "new_market"
  | "product_expansion"
  | "operational_improvement"
  | "partnership"
  | "acquisition"
  | "technology_adoption"

export type StrategicInitiativeStatus = "evaluating" | "approved" | "in_progress" | "complete" | "cancelled"

export type ExperimentCategory =
  | "pricing"
  | "product_feature"
  | "marketing_channel"
  | "sales_process"
  | "customer_experience"

export type ExperimentStatus = "planned" | "running" | "analyzing" | "complete" | "abandoned"

export type ExperimentOutcome = "positive" | "negative" | "neutral" | "inconclusive"

export type ExperimentDecision = "scale" | "iterate" | "abandon" | "pending"

export interface NorthStarMetric {
  metric_name: string
  current_value?: number
  target_value?: number
  target_date?: string
  why_this_metric?: string
}

export interface StrategicPriority {
  priority_name: string
  description?: string
  timeframe?: string
  owner?: string
}

export interface StrategicVision {
  three_year_vision?: string
  north_star_metric?: NorthStarMetric
  strategic_priorities?: StrategicPriority[]
}

export interface KeyResult {
  description: string
  target?: number
  current?: number
  status?: KeyResultStatus
}

export interface GoalMilestone {
  name: string
  due_date?: string
  status?: MilestoneStatus
}

export interface GoalBlocker {
  description: string
  severity?: BlockerSeverity
  resolution_plan?: string
}

export interface StrategicGoal {
  id: string
  title: string
  category?: GoalCategory
  description?: string
  target_metric?: string
  current_value?: number
  target_value?: number
  unit?: string
  start_date?: string
  target_date?: string
  status?: GoalStatus
  progress_percentage?: number
  confidence_level?: number
  owner?: string
  why_this_goal?: string
  success_criteria?: string[]
  related_sections?: SectionSlug[]
  dependencies?: string[]
  key_results?: KeyResult[]
  milestones?: GoalMilestone[]
  blockers?: GoalBlocker[]
  ai_generated?: boolean
  ai_recommended_actions?: string[]
  last_ai_check_in?: string
  created_date?: string
  last_updated?: string
}

export interface RoadmapInitiative {
  name: string
  description?: string
  owner?: string
  status?: InitiativeStatus
}

export interface RoadmapPhase {
  phase_number: number
  name: string
  start_date?: string
  end_date?: string
  objectives?: string[]
  initiatives?: RoadmapInitiative[]
}

export interface Roadmap {
  id: string
  name: string
  type?: RoadmapType
  timeframe?: RoadmapTimeframe
  description?: string
  phases?: RoadmapPhase[]
  status?: RoadmapStatus
  created_date?: string
  last_updated?: string
  ai_generated?: boolean
}

export interface QuarterlyProjection {
  quarter: string
  revenue_target?: number
  expense_budget?: number
  profit_target?: number
}

export interface BusinessPlan {
  id: string
  name: string
  type?: PlanType
  fiscal_year?: string
  executive_summary?: string
  financial_projections?: {
    quarters?: QuarterlyProjection[]
  }
  strategic_initiatives?: string[]
  status?: RoadmapStatus
  version?: number
  created_date?: string
  last_updated?: string
  ai_generated?: boolean
}

export interface StrategicInitiative {
  id: string
  name: string
  type?: StrategicInitiativeType
  description?: string
  status?: StrategicInitiativeStatus
  progress_percentage?: number
  timeline?: {
    start_date?: string
    target_completion?: string
  }
  expected_outcomes?: {
    revenue_impact?: number
    cost_impact?: number
    strategic_value?: string
  }
  created_date?: string
  last_updated?: string
}

export interface ExperimentResults {
  outcome?: ExperimentOutcome
  findings?: string
}

export interface ExperimentDecisionInfo {
  decision?: ExperimentDecision
  rationale?: string
}

export interface Experiment {
  id: string
  name: string
  category?: ExperimentCategory
  hypothesis?: string
  expected_outcome?: string
  status?: ExperimentStatus
  results?: ExperimentResults
  decision?: ExperimentDecisionInfo
  created_date?: string
  last_updated?: string
}

export interface GoalsReview {
  goals_achieved?: number
  goals_missed?: number
  key_achievements?: string[]
  lessons_learned?: string[]
}

export interface FinancialPerformanceReview {
  revenue_vs_target?: number
  expenses_vs_budget?: number
}

export interface QuarterlyReview {
  id: string
  quarter: string
  review_date?: string
  goals_review?: GoalsReview
  financial_performance?: FinancialPerformanceReview
  priorities_next_quarter?: string[]
  created_date?: string
}

export interface StrategyHealthComponents {
  goal_progress?: number
  financial_performance?: number
  execution_quality?: number
  team_alignment?: number
}

export interface AIRecommendation {
  recommendation: string
  priority?: "critical" | "high" | "medium" | "low"
  reasoning?: string
  expected_impact?: string
  created_date?: string
}

export interface StrategyHealth {
  overall_score?: number
  components?: StrategyHealthComponents
  last_calculated?: string
  ai_recommendations?: AIRecommendation[]
}

export interface StrategyData {
  section_meta?: SectionMetadata
  vision?: StrategicVision
  goals?: StrategicGoal[]
  roadmaps?: Roadmap[]
  business_plans?: BusinessPlan[]
  marketing_plans?: BusinessPlan[]
  sales_plans?: BusinessPlan[]
  strategic_initiatives?: StrategicInitiative[]
  experiments?: Experiment[]
  quarterly_reviews?: QuarterlyReview[]
  strategy_health?: StrategyHealth
}

// ============================================
// SECTION DATA TYPE MAP
// ============================================
export interface SectionDataMap {
  identity: IdentityData
  people: PeopleData
  offerings: OfferingsData
  audience: AudienceData
  brand: BrandData
  competition: CompetitionData
  technology: TechnologyData // Updated from any to TechnologyData
  marketing: MarketingData
  sales: SalesData
  operations: OperationsData
  finance: FinanceData
  strategy: StrategyData
}

export type SectionData<T extends SectionSlug> = SectionDataMap[T]

// ============================================
// TENANT & USER TYPES
// ============================================
export interface Tenant {
  id: string
  name: string
  slug: string
  type: OrganizationType
  settings?: Record<string, unknown>
  created_at: string
  updated_at: string
}

export interface User {
  id: string
  email: string
  name?: string
  avatar_url?: string
  metadata?: Record<string, unknown>
  created_at: string
  updated_at: string
}

export interface Membership {
  id: string
  user_id: string
  tenant_id: string
  role: MembershipRole
  permissions?: Record<string, boolean>
  created_at: string
  updated_at: string
}

// ============================================
// DOCUMENT & RAG TYPES
// ============================================
export type DocumentDomain = "strategy" | "marketing" | "sales" | "ops" | "finance" | "brand" | "people" | "technology"

export type DocumentPhase = "intake" | "strategy" | "activation" | "continuity"

export type DocumentSensitivity = "low" | "medium" | "high"

export interface DocumentMetadata {
  tenantId?: string
  brand: "biziq"
  system: "tenant" | "system"
  scope: "tenant" | "global"
  domain: DocumentDomain
  section: SectionSlug
  phase: DocumentPhase
  sensitivity: DocumentSensitivity
  rag_allowed: boolean
  version?: string
  source_item_id?: string
}

export interface Document {
  id: string
  tenant_id?: string
  title: string
  body: string
  metadata: DocumentMetadata
  source_section?: SectionSlug
  source_id?: string
  created_at: string
  updated_at: string
}

export interface DocEmbedding {
  id: string
  document_id: string
  tenant_id: string
  embedding?: number[]
  chunk_index: number
  chunk_text?: string
  metadata: DocumentMetadata
  created_at: string
}

export interface MemoryEmbedding {
  id: string
  tenant_id?: string
  content: string
  kind: "fact" | "insight" | "pattern" | "preference"
  metadata: Record<string, unknown>
  embedding?: number[]
  created_at: string
}

// ============================================
// SECTION METADATA & CONFIG
// ============================================
export interface SectionConfig {
  slug: SectionSlug
  label: string
  description: string
  icon: string
  domain: DocumentDomain
  estimatedTime: string
  requiredFields: string[]
}

export const SECTION_CONFIGS: Record<SectionSlug, SectionConfig> = {
  identity: {
    slug: "identity",
    label: "Identity",
    description: "Core purpose, mission, vision, and values",
    icon: "Building2",
    domain: "strategy",
    estimatedTime: "10-15 min",
    requiredFields: ["legal_name", "mission", "vision"],
  },
  people: {
    slug: "people",
    label: "People",
    description: "Team, advisors, and partners",
    icon: "Users",
    domain: "people",
    estimatedTime: "15-20 min",
    requiredFields: ["team_members"],
  },
  offerings: {
    slug: "offerings",
    label: "Offerings",
    description: "Products, services, and programs",
    icon: "Package",
    domain: "strategy",
    estimatedTime: "20-30 min",
    requiredFields: ["products", "services"],
  },
  audience: {
    slug: "audience",
    label: "Audience",
    description: "Customer segments and journeys",
    icon: "Target",
    domain: "marketing",
    estimatedTime: "15-20 min",
    requiredFields: ["segments"],
  },
  brand: {
    slug: "brand",
    label: "Brand",
    description: "Visual and verbal identity",
    icon: "Palette",
    domain: "brand",
    estimatedTime: "15-20 min",
    requiredFields: ["positioning_statement", "tone_of_voice"],
  },
  competition: {
    slug: "competition",
    label: "Competition",
    description: "Competitors and market positioning",
    icon: "Swords",
    domain: "strategy",
    estimatedTime: "15-20 min",
    requiredFields: ["competitors"],
  },
  technology: {
    slug: "technology",
    label: "Technology",
    description: "Tools, systems, and integrations",
    icon: "Cpu",
    domain: "technology",
    estimatedTime: "10-15 min",
    requiredFields: ["systems"], // Assuming 'systems' is a placeholder for tools moved from operations
  },
  marketing: {
    slug: "marketing",
    label: "Marketing",
    description: "Channels, campaigns, and funnels",
    icon: "Megaphone",
    domain: "marketing",
    estimatedTime: "15-20 min",
    requiredFields: ["channels"],
  },
  sales: {
    slug: "sales",
    label: "Sales",
    description: "Sales model, pipeline, and pricing",
    icon: "TrendingUp",
    domain: "sales",
    estimatedTime: "15-20 min",
    requiredFields: ["sales_model", "pipelines"],
  },
  operations: {
    slug: "operations",
    label: "Operations",
    description: "Processes, SOPs, and logistics",
    icon: "Settings",
    domain: "ops",
    estimatedTime: "15-20 min",
    requiredFields: ["core_processes"],
  },
  finance: {
    slug: "finance",
    label: "Finance",
    description: "Revenue model and unit economics",
    icon: "DollarSign",
    domain: "finance",
    estimatedTime: "15-20 min",
    requiredFields: ["revenue_model"],
  },
  strategy: {
    slug: "strategy",
    label: "Strategy",
    description: "Goals, plans, and execution roadmaps",
    icon: "Compass",
    domain: "strategy",
    estimatedTime: "30-45 min",
    requiredFields: ["strategic_vision", "goals"],
  },
}

export const SECTION_ORDER: SectionSlug[] = [
  "identity",
  "people",
  "offerings",
  "audience",
  "brand",
  "competition",
  "technology",
  "marketing",
  "sales",
  "operations",
  "finance",
  "strategy",
]

// ============================================
// GLOBAL METADATA TYPES
// ============================================

export interface SectionCompletionInfo {
  completion: number
  weight: number
}

export interface ProfileCompletion {
  overall_percentage: number

  tier1_sections: {
    identity: SectionCompletionInfo
    people: SectionCompletionInfo
    offerings: SectionCompletionInfo
    audience: SectionCompletionInfo
    finance: SectionCompletionInfo
    strategy: SectionCompletionInfo
  }
  tier1_completion: number

  tier2_sections: {
    brand: SectionCompletionInfo
    competition: SectionCompletionInfo
    marketing: SectionCompletionInfo
    sales: SectionCompletionInfo
    operations: SectionCompletionInfo
    strategy_advanced: SectionCompletionInfo // Note: This might need mapping if strategy is the only "advanced" section
  }
  tier2_completion: number

  milestones_achieved?: {
    milestone_name: string
    achieved_date: string
    sections_unlocked: string[]
  }[]

  next_recommended_section?: SectionSlug
  next_recommended_action?: string
  completion_reward?: string
}

export interface FieldSecurity {
  sensitivity_level: FieldSensitivity
  encryption_required?: boolean
  pii_data?: boolean
  financial_data?: boolean
  access_log_required?: boolean
  export_allowed?: boolean
  ai_training_allowed?: boolean
}

export interface AIMetadata {
  ai_generated: boolean
  ai_confidence_score?: number
  ai_model_version?: string
  generated_date?: string
  human_reviewed?: boolean
  human_review_date?: string
  human_edited?: boolean
  needs_verification?: boolean
  verification_reason?: string
  source_sections?: SectionSlug[]
  reasoning_path?: string
}

export interface ChangeLogEntry {
  date: string
  user: string
  field_changed: string
  old_value: unknown
  new_value: unknown
  change_type: "create" | "update" | "delete"
}

export interface RevisionSnapshot {
  version: number
  snapshot: Record<string, unknown>
  date: string
}

export interface VersionMetadata {
  version: number
  created_date: string
  last_updated: string
  updated_by?: string
  change_log?: ChangeLogEntry[]
  revision_history?: RevisionSnapshot[]
}

export interface IntegrationMetadata {
  synced_from?: string
  sync_frequency?: "real_time" | "hourly" | "daily" | "manual"
  last_synced?: string
  sync_status?: "synced" | "pending" | "error"
  sync_errors?: string[]
  manual_override?: boolean
  override_reason?: string
}
